import React, { useMemo, useState } from "react";
import { Link, useParams, useLocation, useNavigate } from "react-router-dom";
import {
  Layers,
  ArrowRight,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  FolderOpen,
  LayoutGrid,
  ChevronLeft,
  ClipboardCheck,
  BarChart2,
  Users,
} from "lucide-react";
import ApplicationAnalysisReport from "../components/ApplicationAnalysisReport";

// ✅ SYSTEM IMPORTS
import { PERMS } from "../../../utils/perms";
import { useAccess } from "../../../utils/useAccess";
import PageGate from "../../../components/PageGate";
import AccessGate from "../../../components/AccessGate";

import {
  useGetActiveUserCategoriesQuery,
  useGetDietCategoryCountsQuery,
} from "../api/probationerApplicationApi";

/**
 * =========================
 * UI ATOMS
 * =========================
 */
function safeMsg(x: any) {
  return String(
    x?.data?.message || x?.message || x?.error || "Something went wrong"
  );
}

function CategoryCard({
  id,
  name,
  dietId,
  count,
}: {
  id: number;
  name: string;
  dietId: string;
  count?: number;
}) {
  return (
    <Link
      to={`/probationer-applications/admin/submissions/diets/${dietId}/category/${id}`}
      state={{ categoryName: name, categoryId: id, dietId }}
      className="group relative bg-white border border-gray-200 rounded-[32px] p-8 transition-all hover:shadow-2xl hover:shadow-gray-200 hover:-translate-y-1 overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-24 h-24 bg-gray-50 rounded-bl-[60px] -mr-8 -mt-8 transition-colors group-hover:bg-blue-50" />
      <div className="relative z-10 flex flex-col h-full">
        <div className="flex items-start justify-between mb-6">
          <div className="w-12 h-12 bg-gray-100 rounded-2xl flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-all shadow-sm">
            <FolderOpen className="w-6 h-6" />
          </div>
          <div className="text-right">
            <div className="text-3xl font-black text-gray-900 leading-none tabular-nums">
              {count ?? 0}
            </div>
            <div className="text-[10px] font-black uppercase tracking-widest text-gray-400 mt-1">
              Application{count === 1 ? "" : "s"}
            </div>
          </div>
        </div>
        <h3 className="text-xl font-black text-gray-900 tracking-tight mb-2 group-hover:text-blue-600 transition-colors">
          {name}
        </h3>
        <p className="text-sm font-medium text-gray-500 leading-relaxed mb-6">
          Analyze submissions and manage workflow for this category.
        </p>
        <div className="mt-auto pt-6 border-t border-gray-50 flex items-center justify-between">
          <span className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 group-hover:text-gray-900 transition-colors">
            Enter Category
          </span>
          <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-all">
            <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </div>
    </Link>
  );
}

/**
 * =========================
 * MAIN PAGE
 * =========================
 */
export default function AdminProbationerApplicationDietCategoriesPage() {
  const { dietId } = useParams<{ dietId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { can } = useAccess();

  const {
    data: categories = [],
    isLoading,
    error,
    refetch,
  } = useGetActiveUserCategoriesQuery();

  const { data: catCounts } = useGetDietCategoryCountsQuery(
    { dietId: dietId! },
    { skip: !dietId }
  );
  const counts = catCounts?.counts ?? {};

  const [localError, setLocalError] = useState<string | null>(null);
  const [localSuccess, setLocalSuccess] = useState<string | null>(null);
  const [showAnalysis, setShowAnalysis] = useState(false);

  const ALLOWED_KEYWORDS = ["probationer", "student", "technician"];

  const list = useMemo(
    () =>
      (Array.isArray(categories) ? categories : []).filter((c: any) =>
        ALLOWED_KEYWORDS.some((kw) => c.name?.toLowerCase().includes(kw))
      ),
    [categories]
  );
  const diet = catCounts?.diet;
  const dietTitle =
    diet?.title || (location.state as any)?.dietTitle || `Diet System Record`;

  const fmtDate = (d?: string | null) =>
    d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : null;
  const dietRange = diet ? [fmtDate(diet.start_date), fmtDate(diet.end_date)].filter(Boolean).join(" – ") : "";

  return (
    <PageGate perm={PERMS.application_diet.allCategoriesAccess}>
      <div className="max-w-7xl mx-auto p-6 space-y-8 pb-20">
        {/* REFINED HEADER SECTION */}
        <div className="space-y-6">
          {/* Breadcrumb / Back Row */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="group flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-gray-400 hover:text-gray-900 transition-colors"
            >
              <div className="p-2 rounded-lg bg-white border border-gray-100 group-hover:border-gray-900 transition-all">
                <ChevronLeft className="w-4 h-4" />
              </div>
              Back to Diets
            </button>

            <button
              onClick={() => refetch()}
              disabled={isLoading}
              className="p-2.5 bg-white border border-gray-100 rounded-xl hover:bg-gray-50 transition-all shadow-sm"
            >
              <RefreshCw
                className={`w-4 h-4 text-gray-500 ${
                  isLoading ? "animate-spin" : ""
                }`}
              />
            </button>
          </div>

          {/* Title & Action Row */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 bg-white border border-gray-100 p-6 rounded-[32px] shadow-sm">
            <div className="space-y-1">
              <h1 className="text-3xl font-black text-gray-900 tracking-tight">
                Diet Categories
              </h1>
              <div className="flex items-center gap-2 flex-wrap">
                {diet?.status && (
                  <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${diet.is_active ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>
                    {diet.status}
                  </span>
                )}
                <p className="text-sm font-bold text-gray-500">{dietTitle}</p>
                {dietRange && (
                  <span className="text-[11px] font-bold text-gray-400">· {dietRange}</span>
                )}
                <span className="px-2 py-0.5 rounded-md bg-gray-900 text-white text-[10px] font-black uppercase tracking-widest">
                  {catCounts?.total ?? 0} Total Application{catCounts?.total === 1 ? "" : "s"}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {/* Assessment Buttons - Using distinct icons and outline style */}
              <button
                onClick={() => setShowAnalysis(true)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-[11px] font-bold uppercase tracking-tight text-gray-700 transition-all"
              >
                <BarChart2 className="w-4 h-4 text-indigo-500" />
                Diet Analysis
              </button>

              <Link
                to={`/application-diets/application-assessment/${dietId}`}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-[11px] font-bold uppercase tracking-tight text-gray-700 transition-all"
              >
                <ClipboardCheck className="w-4 h-4 text-blue-500" />
                Application Assessment
              </Link>

              {/* Where an application is placed with a reviewer. */}
              <AccessGate perm={PERMS.application_diet?.autoAssignToCommittee}>
                <Link
                  to={`/probationer-applications/admin/submissions/diets/${dietId}/assignments`}
                  className="flex items-center gap-2 px-6 py-2.5 bg-gray-900 text-white rounded-xl text-[11px] font-black uppercase tracking-widest hover:bg-blue-600 transition-all shadow-lg shadow-gray-200"
                >
                  <Users className="w-4 h-4" />
                  Review Assignment
                </Link>
              </AccessGate>

            </div>
          </div>
        </div>

        {/* FEEDBACK ALERTS */}
        {(localSuccess || localError || error) && (
          <div
            className={`p-4 rounded-[24px] border flex items-center gap-3 animate-in fade-in slide-in-from-top-4 ${
              localSuccess
                ? "bg-green-50 border-green-100 text-green-800"
                : "bg-red-50 border-red-100 text-red-800"
            }`}
          >
            {localSuccess ? (
              <CheckCircle2 className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <div className="text-sm font-bold tracking-tight">
              {localSuccess || localError || safeMsg(error)}
            </div>
          </div>
        )}

        {/* CATEGORY GRID */}
        {isLoading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-64 bg-gray-50 border border-gray-100 rounded-[32px] animate-pulse"
              />
            ))}
          </div>
        ) : list.length === 0 ? (
          <div className="py-20 bg-gray-50 border-2 border-dashed border-gray-200 rounded-[40px] flex flex-col items-center justify-center text-center">
            <div className="w-16 h-16 bg-white rounded-3xl flex items-center justify-center shadow-sm mb-4">
              <LayoutGrid className="w-8 h-8 text-gray-300" />
            </div>
            <h2 className="text-xl font-black text-gray-900 uppercase">
              Empty Diet
            </h2>
            <p className="text-gray-500 max-w-xs mx-auto mt-2 font-medium">
              No active categories found.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {list.map((c: any) => (
              <CategoryCard
                key={c.id}
                id={c.id}
                name={c.name}
                dietId={dietId!}
                count={Number(counts[String(c.id)] ?? 0)}
              />
            ))}
          </div>
        )}

        {/* INFO FOOTER */}
        <div className="mt-12 p-8 bg-blue-50/50 rounded-[32px] border border-blue-100 flex items-start gap-4">
          <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm shrink-0">
            <Layers className="text-blue-600 w-5 h-5" />
          </div>
          <div>
            <h4 className="text-xs font-black text-blue-900 uppercase tracking-widest mb-1">
              Administrative Note
            </h4>
            <p className="text-xs font-bold text-blue-700/70 leading-relaxed max-w-2xl">
              Entry applications are reviewed by the same Membership Committee
              that reviews grade upgrades. Open Review Assignment to place them
              with a reviewer.
            </p>
          </div>
        </div>
      </div>

      {/* Diet-wide analysis modal */}
      {showAnalysis && dietId && (
        <ApplicationAnalysisReport
          scope={{ kind: "diet", dietId, label: dietTitle }}
          onClose={() => setShowAnalysis(false)}
        />
      )}
    </PageGate>
  );
}
